/**
 * Credit packs API slice.
 * Leaf module (Perf 3.1.b/e Step 5). Pulls only the shared chokepoint from
 * ./client; never imports a sibling @/lib/api slice.
 */
import { fetchWithAuth, ApiError } from './client';

export const creditPacksApi = {
  getCreditPacks: async () => {
    try {
      const data = await fetchWithAuth('/json/credit-packs');
      
      if (data.error) {
        throw new ApiError(data.message || 'Failed to fetch credit packs', 400, data);
      }
      
      return data.creditPacks;
    } catch (error) {
      console.error('Error fetching credit packs:', error);
      throw error;
    }
  },

  // Quick purchase with stored card (uses backend token, not passed from frontend)
  quickPurchase: async (creditPackId, promoCode = null) => {
    try {
      const body = { creditPackId };
      if (promoCode) body.promoCode = promoCode;
      const data = await fetchWithAuth('/json/credits/quick-purchase', {
        method: 'POST',
        body,
      });

      // Check if we got credits data back (indicates success)
      if (data.credits) {
        return data;
      }

      // Only throw if there's an actual error (no credits returned)
      if (data.error && !data.credits) {
        throw new ApiError(data.message || 'Failed to process quick purchase', 400, data);
      }

      return data;
    } catch (error) {
      // Backend bug: may return error status but with success data
      // Check if the error contains credits data - if so, treat as success
      if (error.data?.credits) {
        return error.data;
      }
      console.error('Error processing quick purchase:', error);
      throw error;
    }
  },

  // Validate a promo code for a credit pack purchase
  validatePromoCode: async (code, creditPackId) => {
    try {
      const data = await fetchWithAuth('/json/promo-code/validate', {
        method: 'POST',
        body: { code, creditPackId },
      });
      return data.data || data;
    } catch (error) {
      console.error('Error validating promo code:', error);
      throw error;
    }
  },

  // Create a BTCPay invoice for purchasing a credit pack with Bitcoin (web only).
  // Mirrors accountApi.createBtcPayInvoice (Plus) but for credit packs.
  createBtcPayInvoice: async ({ creditPackId }) => {
    const data = await fetchWithAuth('/json/credits/create-btcpay-invoice', {
      method: 'POST',
      body: { creditPackId },
    });

    if (data.error) {
      throw new ApiError(data.message || 'Failed to create Bitcoin invoice', 400, data);
    }

    return data;
  },

  // Check the status of a credit-pack BTCPay invoice.
  checkBtcPayInvoiceStatus: async (invoiceId) => {
    const data = await fetchWithAuth(`/json/credits/btcpay-invoice/${invoiceId}/status`);

    if (data.error) {
      throw new ApiError(data.message || 'Failed to check invoice status', 400, data);
    }

    return data;
  },

  // Get auto top-up settings
  getAutoTopupSettings: async () => {
    try {
      const data = await fetchWithAuth('/json/account/auto-topup');

      if (data.error) {
        throw new ApiError(data.message || 'Failed to fetch auto top-up settings', 400, data);
      }

      return data;
    } catch (error) {
      console.error('Error fetching auto top-up settings:', error);
      throw error;
    }
  },

  // Update auto top-up settings
  updateAutoTopupSettings: async ({ enabled, thresholdCredits, creditPackId, monthlySpendLimit, version, provider, consent, quote, paymentMethodFingerprint, afterPurchase, afterPurchasePaymentId }) => {
    try {
      const data = await fetchWithAuth('/json/account/auto-topup', {
        method: 'POST',
        body: { enabled, thresholdCredits, creditPackId, monthlySpendLimit, version, provider, consent, quote, paymentMethodFingerprint, afterPurchase, afterPurchasePaymentId }
      });

      if (data.error) {
        throw new ApiError(data.message || 'Failed to update auto top-up settings', 400, data);
      }

      return data;
    } catch (error) {
      console.error('Error updating auto top-up settings:', error);
      throw error;
    }
  },

  startWhopAutoTopupSetup: async () => fetchWithAuth('/json/account/auto-topup/whop-setup', { method: 'POST', body: {} }),

  // Record that an auto top-up offer was shown, or declined. Best-effort: the
  // user's answer must never block the surface they were answering from.
  recordAutoTopupOffer: async (action) => {
    try {
      await fetchWithAuth('/json/account/auto-topup/offer', {
        method: 'POST',
        body: { action },
      });
    } catch (_) {}
  },
};
